import React, { useCallback, useEffect } from 'react';

import { DateTime } from 'luxon';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { ModalService } from 'ui-components/services/modal-service';

import { calculateTargetDatetimesFromOperator } from '../../components/date-range-dropdown/calculate-date-range-helper';
import { ErrorModal } from '../../components/modals/error-modal';
import { dateFilterOperators } from '../../containers/base-filter-bar-container/reference-data-criteria-arguments';
import { emptyObject } from 'ui-components/default-props/empty';
import { useQuery } from '../../hooks/routing/use-query';
import Global from '../../store/global-selectors';

const QUERY_START_DATE = 'startDate';
const QUERY_END_DATE = 'endDate';
const INSIGHTS_DATE_FORMAT = 'yyyy-MM-dd';

const decodeQueryParam = (encodedURIString) => {
  try {
    return decodeURIComponent(encodedURIString);
  } catch {
    return undefined;
  }
};

const getStartRangeFromQueryStringOrDefault = (query) => {
  const queryStartDate = query.get(QUERY_START_DATE);

  if (queryStartDate) {
    return decodeQueryParam(queryStartDate);
  }
};

const getEndRangeFromQueryStringOrDefault = (query) => {
  const queryEndDate = query.get(QUERY_END_DATE);
  if (queryEndDate) {
    return decodeQueryParam(queryEndDate);
  }
};

const onFetchDataError = () => {
  ModalService.launchModal({
    component: ErrorModal,
    props: {
      title: <FormattedMessage defaultMessage='Something went wrong' description='insights error modal title' id='7amaVR' />,
      message: (
        <FormattedMessage
          defaultMessage='Your date range may be invalid. Please try again with an updated range.'
          description='insights error modal description'
          id='Oxr06v'
        />
      ),
    },
  });
};

/**
 * @typedef {object} ApiResponseData
 * @property {boolean} isInsightsEnabled
 */

/**
 * @typedef {object} ApiResponse
 * @property {ApiResponseData} data
 */

/**
 * @param {(arg: { startDate: string, endDate: string, storeId?: string }) => Promise<ApiResponse>} api
 * @returns {{
 *  chartData: object,
 *  startDateISO: string,
 *  endDateISO: string,
 *  selectedStore: object,
 *  setSelectedStoreCallback: (function(object): void)
 *  setDateRangeCallback: (function({startDate: string, endDate: string}): void),
 *  isLoading: boolean,
 *  isInsightsEnabled: boolean,
 * }}
 */
export const useInsights = (api) => {
  const query = useQuery();
  const location = useLocation();
  const history = useHistory();

  const [startDateISO, setStartDateISO] = useAsyncSafeState(() => getStartRangeFromQueryStringOrDefault(query));
  const [endDateISO, setEndDateISO] = useAsyncSafeState(() => getEndRangeFromQueryStringOrDefault(query));
  const [selectedStore, setSelectedStore] = useAsyncSafeState(undefined);
  const [chartData, setChartData] = useAsyncSafeState(emptyObject);
  const [isLoading, setIsLoading] = useAsyncSafeState(true);
  const [isInsightsEnabled, setIsInsightsEnabled] = useAsyncSafeState(true);

  const sellerTimeZone = useSelector(Global.selectSellerTimeZoneIANA);

  const getInsightsDateFromISO = useCallback(
    (dateString) => {
      return DateTime.fromISO(dateString).setZone(sellerTimeZone).toFormat(INSIGHTS_DATE_FORMAT);
    },
    [sellerTimeZone],
  );

  const fetchInsightData = useCallback(
    async (start, end) => {
      setIsLoading(true);

      try {
        const response = await api({
          startDate: getInsightsDateFromISO(start),
          endDate: getInsightsDateFromISO(end),
          ...(selectedStore && { storeId: selectedStore.storeId }),
        });
        const isEnabled = response?.data?.isInsightsEnabled ?? true;
        setIsLoading(false);
        setIsInsightsEnabled(isEnabled);
        setChartData(isEnabled && response?.data ? response.data : emptyObject);
      } catch {
        setChartData(emptyObject);
        setIsLoading(false);
        onFetchDataError();
      }
    },
    [api, getInsightsDateFromISO, selectedStore, setChartData, setIsInsightsEnabled, setIsLoading],
  );

  useEffect(() => {
    if (!startDateISO || !endDateISO) {
      const { startDate, endDate } = calculateTargetDatetimesFromOperator(dateFilterOperators.dateLast30Days, sellerTimeZone);

      setStartDateISO(DateTime.fromISO(startDate).setZone(sellerTimeZone).toISO());
      setEndDateISO(DateTime.fromISO(endDate).setZone(sellerTimeZone).toISO());
    }
  }, [startDateISO, endDateISO, setStartDateISO, setEndDateISO, sellerTimeZone]);

  useEffect(() => {
    if (startDateISO && endDateISO) {
      fetchInsightData(startDateISO, endDateISO);
    }
  }, [endDateISO, fetchInsightData, startDateISO]);

  const setDateRangeCallback = useCallback(
    ({ startDate: startISO, endDate: endISO }) => {
      if (startISO && endISO) {
        query.set(QUERY_START_DATE, encodeURIComponent(getInsightsDateFromISO(startISO)));
        query.set(QUERY_END_DATE, encodeURIComponent(getInsightsDateFromISO(endISO)));
        history.push(`${location.pathname}?${query.toString()}`);
      } else {
        query.delete(QUERY_START_DATE);
        query.delete(QUERY_END_DATE);
        history.push(location.pathname);
      }

      setStartDateISO(startISO);
      setEndDateISO(endISO);
    },
    [getInsightsDateFromISO, history, location.pathname, query, setEndDateISO, setStartDateISO],
  );

  return {
    chartData,
    startDateISO,
    endDateISO,
    selectedStore,
    setSelectedStoreCallback: setSelectedStore,
    setDateRangeCallback,
    isLoading,
    isInsightsEnabled,
  };
};
